import { defineStore } from "pinia";

import AuthService from "../services/auth.service";
import { newCache } from "../utils/cache";

const THREE_MINS = 3 * 60 * 1000;
const authCache = newCache(THREE_MINS);

// Define types for the store state
export interface ACL {
  URL: string;
  Status: number;
  AutoBrowseUsers?: boolean;
  Config?: {
    UriToken?: string;
  };
  ACLID?: string;
  [key: string]: any;
}

export interface User {
  ACLs?: ACL[];
  [key: string]: any;
}

export interface Theme {
  Accent: string;
  HighAccessibility: boolean;
  [key: string]: any;
}

export interface FetchOptions {
  force?: boolean;
  ttl?: number;
}

const storeDefinition = {
  state: () => ({
    loggedIn: false,
    user: null as User | null,
    userFetchedAt: null as number | null,
    theme: null as Theme | null,
    themeFetchedAt: null as number | null,
    autoBrowseDismissed: false,
  }),

  actions: {
    async login(userInput: any) {
      try {
        const user = await AuthService.login(userInput);
        this.loggedIn = true;
        return user as User;
      } catch (err) {
        this.resetAndClearPersisted();
        throw err;
      }
    },

    async fetchStatus(options: FetchOptions = {}) {
      try {
        const user = await authCache({
          state: this,
          key: "user",
          fetchFn: () => AuthService.status(),
          ...options,
        });

        this.loggedIn = true;
        return user as User;
      } catch (err) {
        this.resetAndClearPersisted();
        throw err;
      }
    },

    async fetchTheme(options: FetchOptions = {}) {
      try {
        await authCache({
          state: this,
          key: "theme",
          ttl: 10 * 60 * 1000, // 10 mins
          fetchFn: async () => {
            const response = await AuthService.getTheme();
            return response.data;
          },
          ...options,
        });
        return this.theme?.Accent || "grape";
      } catch (err) {
        return "grape";
      }
    },

    async fetchHighAccessibility(options: FetchOptions = {}) {
      try {
        await authCache({
          state: this,
          key: "theme",
          ttl: 10 * 60 * 1000, // 10 mins
          fetchFn: async () => {
            const response = await AuthService.getTheme();
            return response.data;
          },
          ...options,
        });
        return this.theme?.HighAccessibility || false;
      } catch (err) {
        return false;
      }
    },

    dismissAutoBrowse() {
      this.autoBrowseDismissed = true;
    },

    async logout() {
      try {
        await AuthService.logout();
      } finally {
        this.resetAndClearPersisted();
      }
    },

    hasFreshUser() {
      if (!this.user || !this.userFetchedAt) {
        return false;
      }
      return Date.now() - this.userFetchedAt < THREE_MINS;
    },

    resetAndClearPersisted() {
      this.$reset();
    },
  },
};

// Create the store with proper typing
export const useAuthStore = defineStore("auth", storeDefinition);

// Add persist configuration separately (this avoids TypeScript issues)
(useAuthStore as any).persist = [
  {
    paths: ["user", "userFetchedAt", "autoBrowseDismissed"],
  },
  {
    paths: ["theme", "themeFetchedAt"],
    storage: typeof window !== "undefined" ? localStorage : undefined,
  },
];

export type AuthStore = ReturnType<typeof useAuthStore>;
